import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Leaf, Sparkles, CheckCircle2, Quote, Calendar, Star } from "lucide-react";
import { PRODUCTS, FEATURES, TRUST_BADGES, REVIEWS } from "../data";
import ProductCard from "../components/ProductCard";
import NewsCard from "../components/NewsCard";
import PressStrip from "../components/PressStrip";
import Rating from "../components/Rating";
import useNews from "../useNews";

const HERO_BG =
  "https://images.unsplash.com/photo-1726177973715-9d17703e8d68?crop=entropy&cs=srgb&fm=jpg&w=2200&q=85";

const STRIP = [
  "Hand-harvested",
  "Same-day delivery in Nagpur",
  "FSSAI certified",
  "Small-batch · no chemicals",
  "Family run · est. 2024",
  "Free local delivery before noon",
];

export default function Home({ onAdd, onBuyNow }) {
  const { list: news } = useNews();
  const featured = PRODUCTS.filter((p) =>
    ["dried-oyster", "mushroom-coffee", "growing-kit-home"].includes(p.id)
  );
  const featuredNews = news[0];
  const sideNews = news.slice(1, 3);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative -mt-[120px] pt-36 pb-0 min-h-[100vh] flex flex-col justify-between overflow-hidden bg-ink"
      >
        <div
          className="absolute inset-0 bg-cover bg-center scale-[1.05]"
          style={{ backgroundImage: `url(${HERO_BG})` }}
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-gradient-to-br from-ink/85 via-ink/55 to-ink/15"
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent"
          aria-hidden
        />
        <div
          className="absolute inset-0 opacity-[0.18] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.85'/></svg>\")",
          }}
          aria-hidden
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-16 pt-10 w-full grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8 text-bone">
            <div className="fade-up flex items-center gap-3 text-bone/85">
              <span className="h-1.5 w-1.5 rounded-full bg-butter pulse-dot" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-medium">
                Farm to kitchen
              </span>
              <span className="opacity-40">·</span>
              <span className="text-[11px] tracking-[0.32em] uppercase font-medium">
                Nagpur, MH
              </span>
            </div>

            <h1 className="font-display fade-up-d1 mt-7 text-display-xl text-bone">
              Turnkey solutions{" "}
              <span className="md:hidden">for mushroom cultivation, </span>
              <br className="hidden md:inline" />
              <span className="hidden md:inline">for mushroom cultivation,<br /></span>
              <span className="font-italic text-butter">research &amp; training.</span>
            </h1>

            <div className="fade-up-d2 mt-10 flex flex-wrap items-end gap-x-10 gap-y-6">
              <p className="max-w-md text-bone/80 leading-relaxed text-[15px]">
                Organo Mushroom Research &amp; Training Center — Central
                India's turnkey solution for mushroom cultivation, premium
                spawn, value-added products and hands-on entrepreneur
                training.
              </p>

              <div className="flex flex-wrap gap-3 ml-auto">
                <Link to="/shop" data-testid="hero-cta-shop" className="btn-primary bg-bone text-ink hover:bg-butter">
                  Shop fresh harvest
                  <span className="knob bg-ink text-bone">
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </Link>
                <Link
                  to="/gallery"
                  data-testid="hero-cta-gallery"
                  className="link-underline self-center text-bone text-sm font-medium tracking-wide px-2"
                >
                  View the gallery
                </Link>
              </div>
            </div>
          </div>

          {/* Floating FSSAI sticker */}
          <div className="hidden md:flex md:col-span-4 justify-end fade-up-d3">
            <div className="relative" style={{ "--rot": "-8deg" }}>
              <div className="float-y h-44 w-44 rounded-full bg-butter text-ink grid place-items-center text-center p-6 shadow-stamp ring-1 ring-ink/10">
                <div>
                  <Leaf className="h-5 w-5 mx-auto" />
                  <p className="font-display text-[26px] leading-[1.05] mt-1.5">
                    FSSAI<br />Certified
                  </p>
                  <p className="text-[9px] tracking-[0.22em] uppercase mt-2 opacity-75">
                    Reg. 21250275000103
                  </p>
                </div>
              </div>
              <span className="absolute -bottom-3 -left-3 inline-flex items-center gap-1.5 rounded-full bg-bone text-ink px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase shadow-soft">
                <Sparkles className="h-3 w-3 text-coral" /> Fresh today
              </span>
            </div>
          </div>
        </div>

        {/* Bottom value strip */}
        <div className="relative z-10 border-t border-bone/15 bg-black/30 backdrop-blur-md">
          <div className="flex animate-marquee whitespace-nowrap py-4 gap-10 text-bone/85">
            {[...STRIP, ...STRIP, ...STRIP].map((s, i) => (
              <span key={i} className="inline-flex items-center gap-6">
                <span className="text-[11px] tracking-[0.32em] uppercase">{s}</span>
                <span className="text-butter">✦</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* STORY STRIP */}
      <section className="relative py-28 md:py-32">
        <span className="stamp-num absolute -top-2 right-6 md:right-12">N° 01</span>
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-6 relative">
            <div className="aspect-[4/5] rounded-4xl overflow-hidden img-zoom shadow-soft">
              <img
                src="https://customer-assets.emergentagent.com/job_organo-fresh/artifacts/yn62qasf_organo%20mushroom.jpg"
                alt="Inside the Organo Mushroom unit"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-8 -right-4 md:-right-8 bg-sage text-bone rounded-full h-32 w-32 md:h-40 md:w-40 grid place-items-center text-center shadow-stamp" style={{ "--rot": "6deg" }}>
              <div className="float-y">
                <p className="font-display text-[20px] md:text-[24px] leading-tight">
                  Est.<br />
                  <span className="font-italic">2024</span>
                </p>
                <p className="text-[9px] uppercase tracking-[0.22em] mt-1 opacity-80">
                  Manish Nagar
                </p>
              </div>
            </div>
          </div>

          <div className="md:col-span-6">
            <p className="section-num">
              <strong>01</strong> Our story
            </p>
            <h2 className="font-display mt-5 text-display-lg text-ink tracking-tightest">
              A Central India centre,
              <br />
              <span className="font-italic text-coral">built on research.</span>
            </h2>
            <p className="mt-6 text-ink/70 leading-relaxed text-[15px] max-w-lg">
              Organo Mushroom Research &amp; Training Center provides turnkey
              solutions for mushroom cultivation. We make premium spawn,
              cultivation kits, value-added mushroom products, and we train
              entrepreneurs on edible and medicinal cultivation.
            </p>

            <figure className="mt-8 border-l-2 border-coral pl-5 max-w-lg">
              <Quote className="h-4 w-4 text-coral/70" />
              <blockquote className="font-italic text-[26px] leading-[1.2] text-ink/90 mt-2">
                "Picked, packed, refrigerated — all within minutes of cutting."
              </blockquote>
            </figure>

            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
              {FEATURES.map((f) => (
                <li
                  key={f.title}
                  data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex gap-3"
                >
                  <CheckCircle2 className="h-5 w-5 text-coral flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-ink text-[15px]">{f.title}</p>
                    <p className="mt-0.5 text-sm text-ink/60 leading-relaxed">
                      {f.body}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            <Link to="/about" className="link-underline inline-flex items-center gap-2 mt-10 text-sm font-medium">
              Read the full story <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TRUST MARQUEE */}
      <div className="border-y border-ink/10 bg-sageFog overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap py-6 gap-12">
          {[...TRUST_BADGES, ...TRUST_BADGES, ...TRUST_BADGES].map((b, i) => (
            <span
              key={i}
              data-testid={`trust-badge-${i}`}
              className="font-display text-[28px] md:text-[36px] text-ink/75 inline-flex items-center gap-12"
            >
              <span className="font-italic">{b}</span>
              <span className="text-coral">✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* FEATURED SHOP */}
      <section className="relative py-28 md:py-32">
        <span className="stamp-num absolute -top-2 right-6 md:right-12">N° 02</span>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-8">
              <p className="section-num">
                <strong>02</strong> Today's harvest
              </p>
              <h2 className="font-display mt-5 text-display-lg text-ink tracking-tightest">
                Picked this morning,
                <br />
                <span className="font-italic text-coral">on your table tonight.</span>
              </h2>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <Link to="/shop" className="btn-primary">
                Shop everything
                <span className="knob">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </Link>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {featured.map((p, idx) => (
              <ProductCard
                key={p.id}
                product={p}
                onAdd={onAdd}
                onBuyNow={onBuyNow}
                index={idx}
                total={featured.length}
              />
            ))}
          </div>
        </div>
      </section>

      {/* PRESS / ACCREDITATIONS */}
      <PressStrip variant="light" />

      {/* CUSTOMER REVIEWS BAND */}
      <ReviewsBand />

      {/* WORKSHOP TEASER */}
      <section className="relative bg-sageDeep text-bone overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.82' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          }}
          aria-hidden
        />
        <span className="stamp-num absolute -top-2 right-6 md:right-12 text-bone/10">N° 03</span>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-32 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <p className="section-num text-bone/55">
              <strong className="text-butter">03</strong> Hands-on workshop
            </p>
            <h2 className="font-display mt-5 text-display-lg text-bone tracking-tightest">
              Learn from the
              <br />
              <span className="font-italic text-butter">research centre.</span>
            </h2>
            <p className="mt-7 text-bone/80 max-w-lg leading-relaxed text-[15px]">
              Three programmes — entrepreneur foundation (Oyster · Milky ·
              Paddy), Shitake &amp; Ganoderma, and Cordyceps. Hands-on at our
              Nagpur unit. Certificate, starter spawn and WhatsApp support
              included.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/workshop" data-testid="workshop-cta" className="btn-primary bg-butter text-ink hover:bg-bone">
                See training tracks
                <span className="knob bg-ink text-butter">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </Link>
              <span className="self-center text-bone/55 text-sm font-italic text-lg">
                From ₹6,999 / seat · limited seats
              </span>
            </div>
          </div>

          <div className="md:col-span-6 relative">
            <div className="aspect-[1/2] max-h-[640px] mx-auto rounded-4xl overflow-hidden shadow-hoverCard tilt-l bg-bone p-3 md:p-4">
              <div className="w-full h-full rounded-3xl overflow-hidden bg-bone">
                <img
                  src="/img/training/poster-oyster-milky-paddy.jpg"
                  alt="Mushroom training programme poster"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            <div className="absolute -top-6 -right-4 sticker bg-coral text-bone shadow-stamp" style={{ transform: "rotate(8deg)" }}>
              ✦ Limited seats
            </div>
          </div>
        </div>
      </section>

      {/* LATEST NEWS */}
      <section className="relative py-28 md:py-32">
        <span className="stamp-num absolute -top-2 right-6 md:right-12">N° 04</span>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-8">
              <p className="section-num">
                <strong>04</strong> Latest news
              </p>
              <h2 className="font-display mt-5 text-display-lg text-ink tracking-tightest">
                Notes from the
                <br />
                <span className="font-italic text-coral">growing room.</span>
              </h2>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <Link to="/news" className="link-underline inline-flex items-center gap-2 text-sm font-medium">
                All stories <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="mt-14 grid md:grid-cols-12 gap-10">
            <div className="md:col-span-7">
              {featuredNews && <NewsCard item={featuredNews} variant="default" />}
            </div>
            <div className="md:col-span-5 flex flex-col gap-10">
              {sideNews.map((n) => (
                <NewsCard key={n.slug} item={n} variant="default" />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative bg-coral text-bone overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <p className="section-num text-bone/70">
              <strong className="text-butter">·</strong> Place an order
            </p>
            <h3 className="font-display mt-3 text-display-md text-bone tracking-tightest">
              Order before noon,
              <br />
              <span className="font-italic text-butter">eat fresh tonight.</span>
            </h3>
          </div>
          <div className="md:col-span-4 flex md:justify-end gap-3 flex-wrap">
            <Link to="/shop" className="btn-primary bg-bone text-ink hover:bg-butter">
              Shop now
              <span className="knob bg-ink text-butter">
                <ArrowUpRight className="h-4 w-4" />
              </span>
            </Link>
            <Link to="/workshop" className="inline-flex items-center gap-2 text-bone link-underline text-sm font-medium self-center">
              <Calendar className="h-4 w-4" /> Book workshop
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function ReviewsBand() {
  // Collect a few highest-rated reviews across products
  const picks = [
    { pid: "dried-oyster", idx: 0 },
    { pid: "growing-kit-home", idx: 0 },
    { pid: "mushroom-coffee", idx: 0 },
    { pid: "spawn-djamore-pink", idx: 0 },
  ]
    .map(({ pid, idx }) => {
      const product = PRODUCTS.find((p) => p.id === pid);
      const review = REVIEWS[pid]?.[idx];
      return product && review ? { product, review } : null;
    })
    .filter(Boolean);

  return (
    <section className="relative py-28 md:py-32 bg-sageFog border-y border-ink/10">
      <span className="stamp-num absolute -top-2 right-6 md:right-12">N° R</span>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <p className="section-num">
              <strong>·</strong> What our customers say
            </p>
            <h2 className="font-display mt-5 text-display-lg text-ink tracking-tightest">
              Loved by Nagpur,
              <br />
              <span className="font-italic text-coral">delivered fresh.</span>
            </h2>
          </div>
          <div className="md:col-span-5 flex md:justify-end gap-6">
            <div className="text-right">
              <div className="inline-flex items-center gap-1.5 mb-1.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="h-5 w-5 fill-butter text-butter" strokeWidth={1.5} />
                ))}
              </div>
              <p className="font-display text-3xl tabular-nums tracking-tightest leading-none">4.8</p>
              <p className="text-xs uppercase tracking-[0.22em] text-muted mt-2">
                avg across 751 reviews
              </p>
            </div>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {picks.map(({ product, review }) => (
            <article
              key={product.id + review.name}
              className="bg-boneSoft rounded-3xl p-6 md:p-7 border border-ink/5 shadow-soft flex flex-col"
            >
              <Rating value={review.rating} showCount={false} />
              <h3 className="font-display text-[22px] mt-3 text-ink leading-snug tracking-tightest">
                "{review.title}"
              </h3>
              <p className="mt-3 text-ink/65 text-sm leading-relaxed flex-grow">
                {review.body}
              </p>
              <div className="mt-5 pt-4 border-t border-ink/5 flex items-center justify-between">
                <p className="text-[13px] font-medium text-ink">{review.name}</p>
                <Link
                  to="/shop"
                  className="text-[11px] uppercase tracking-[0.22em] text-coral hover:underline"
                >
                  {product.name}
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
